import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { withRouter, useLocation } from 'react-router-dom';
import { compose } from 'ramda';

import { withUser } from 'hoc';
import GlobalBanner from 'pureUi/GlobalBanner';

import { StyledLayout, LayoutChildren, LayoutHeader } from './Layout.styles';
import { Footer } from '../../containers/Footer/Footer';
import { EmptyInvHeader, InvHeader } from 'ui/InvHeader';
import { useSelector } from 'react-redux';
import { isAuthenticated as isAuthenticatedSelector, isTA as isTASelector, getCurrentUser } from 'store/modules/auth';
import { readFromSessionStorage } from 'services/windowSessionStorage';

import classnames from 'classnames';
import { ANALYTICS_SESSION_UUID_KEY, APP_ROUTING_PATHS } from 'config';
import { DriftSnippet } from 'integrations/drift/Snippet';
import { WoopraSnippet } from 'integrations/woopra/Snippet';
import { HotjarSnippet } from 'integrations/hotjar/Snippet';
import { GoogleAnalyticsSnippet } from 'integrations/GoogleAnalytics/Snippet';
import { initWoopra } from 'integrations/woopra/initWoopra';
import { initTrackingEvents } from 'integrations/trackingEvents';
import { initAmplitude, setAmplitudeUser } from 'integrations/amplitude/initAmplitude';
import { initClarity } from 'integrations/clarity/initClarity';
import { ChatWidgetSnippet } from 'integrations/chatWidget/Snippet';
import {
  formatDateDisplay,
  formatDateTimeDisplay,
  formatDateTimeDisplayWithoutOffset,
  formatDurationDisplay,
  isoToUnixTimestamp,
  MINUTES,
  SECONDS,
} from 'utils';
import { ErrorBar } from 'ui/NetworkStatusBar';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

const ANCHOR_ORIGIN = Object.freeze({
  vertical: 'bottom',
  horizontal: 'left',
});

/**
 * Drift enabled
 *
 * Compares role to env var to see if drift should be enabled
 *
 * @param {string} role
 * @returns {boolean}
 */

const MAINTENANCE_ACCESS_KEY = 'MAINTENANCE_BYPASS_ACCESS';

export const Layout = ({ children, location: { pathname }, isAppVersionDeprecated, user }) => {
  const { dynamicParameters, dynamicParametersAreEmpty } = useDynamicParameters();
  const queryParams = new URLSearchParams(window.location.search);
  const accessParam = queryParams.get('access');

  // Get access from localStorage as fallback
  const storedAccess = window.localStorage.getItem(MAINTENANCE_ACCESS_KEY);

  // Use query param if present, otherwise fall back to localStorage
  const effectiveAccess = accessParam || storedAccess;

  // Update localStorage if query param is present
  useEffect(() => {
    if (accessParam) {
      window.localStorage.setItem(MAINTENANCE_ACCESS_KEY, accessParam);
    }
  }, [accessParam]);

  const scheduledMaintenanceStartDate =
    effectiveAccess &&
    dynamicParameters.SCHEDULED_MAINTENANCE_START &&
    isoToUnixTimestamp(dynamicParameters.SCHEDULED_MAINTENANCE_START) === parseInt(effectiveAccess)
      ? undefined
      : dynamicParameters.SCHEDULED_MAINTENANCE_START;

  // re-evaluated on an interval so the banner/page copy transitions and the
  // remaining-time countdown update without a manual reload
  const [now, setNow] = useState(() => new Date().getTime());

  useEffect(() => {
    if (!dynamicParameters.SCHEDULED_MAINTENANCE_START) {
      return;
    }
    const intervalId = setInterval(() => setNow(new Date().getTime()), 60 * SECONDS);
    return () => clearInterval(intervalId);
  }, [dynamicParameters.SCHEDULED_MAINTENANCE_START]);

  const isMaintenanceOngoing = new Date(scheduledMaintenanceStartDate).getTime() <= now;

  const scheduledMaintenanceDurationMinutes =
    Number.isFinite(dynamicParameters.SCHEDULED_MAINTENANCE_DURATION_MINUTES) &&
    dynamicParameters.SCHEDULED_MAINTENANCE_DURATION_MINUTES > 0
      ? dynamicParameters.SCHEDULED_MAINTENANCE_DURATION_MINUTES
      : undefined;

  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const isTA = useSelector(isTASelector);
  const currentUser = useSelector(getCurrentUser);

  // read the session ID that got set in src\index.jsx
  const sessionUuid = readFromSessionStorage(ANALYTICS_SESSION_UUID_KEY);

  // if we want certain pages to NOT display the main header, add them
  // into this array
  const location = useLocation();
  const topLevelPath = location.pathname.split('/')[1];
  const pagesToHideHeaderFor = [
    APP_ROUTING_PATHS.sharedLink,
    APP_ROUTING_PATHS.itinerary,
    APP_ROUTING_PATHS.voucher,
    APP_ROUTING_PATHS.otp,
    APP_ROUTING_PATHS.reviews,
  ];
  const pagesToHideFooterFor = [
    APP_ROUTING_PATHS.sharedLink,
    APP_ROUTING_PATHS.itinerary,
    APP_ROUTING_PATHS.voucher,
    APP_ROUTING_PATHS.otp,
  ];

  // Check if we're on the content detail page specifically (matches /content/:uuid pattern)
  // but NOT /content/edit/:uuid or /content/create
  const isContentDetailPage =
    location.pathname.match(/^\/content\/[^\/]+$/) &&
    !location.pathname.includes('/edit') &&
    !location.pathname.includes('/create');

  // note: the slash is important, as app routes are declared with a leading slash
  const showHeader = !pagesToHideHeaderFor.includes(`/${topLevelPath}`) && !isContentDetailPage;
  const showFooter = !pagesToHideFooterFor.includes(`/${topLevelPath}`);
  const showInvHeader = isAuthenticated;
  const showClassicHeader = !isAuthenticated;

  useLayoutEffect(() => {
    !dynamicParametersAreEmpty && initWoopra(dynamicParameters.WOOPRA_DOMAIN);
  }, [dynamicParameters.WOOPRA_DOMAIN, dynamicParametersAreEmpty]);

  useEffect(() => {
    if (currentUser?.email && currentUser?.type) {
      initTrackingEvents({
        email: currentUser.email,
        country: currentUser?.countryCode || null,
        userType: currentUser.type,
        travelAgentId: currentUser?.uuid || null,
      });
    }
  }, [currentUser?.email, currentUser?.type, currentUser?.countryCode, currentUser?.uuid]);

  useEffect(() => {
    if (dynamicParameters.AMPLITUDE_API_KEY) {
      initAmplitude(dynamicParameters.AMPLITUDE_API_KEY);
      if (user?.uuid) {
        setAmplitudeUser(user.uuid, user?.type);
      }
    }
  }, [dynamicParameters.AMPLITUDE_API_KEY, user?.uuid, user?.type]);

  // parity with the hotjar gate above - only record sessions for logged in users
  useEffect(() => {
    if (dynamicParameters.CLARITY_PROJECT_ID && user?.uuid) {
      initClarity(dynamicParameters.CLARITY_PROJECT_ID, sessionUuid, user.uuid);
    }
  }, [dynamicParameters.CLARITY_PROJECT_ID, sessionUuid, user?.uuid]);

  const showChatWidget = useCallback(() => {
    return (
      (isTA ? dynamicParameters.ENABLE_CHAT_TRAVEL_AGENTS : dynamicParameters.ENABLE_CHAT_INTERNAL_USERS) &&
      dynamicParameters.CHAT_WIDGET_URL
    );
  }, [
    dynamicParameters.ENABLE_CHAT_TRAVEL_AGENTS,
    dynamicParameters.ENABLE_CHAT_INTERNAL_USERS,
    dynamicParameters.CHAT_WIDGET_URL,
    isTA,
  ]);

  if (scheduledMaintenanceStartDate && isMaintenanceOngoing) {
    const scheduledMaintenanceEndTime = scheduledMaintenanceDurationMinutes
      ? new Date(scheduledMaintenanceStartDate).getTime() + scheduledMaintenanceDurationMinutes * MINUTES
      : undefined;
    // round remaining time up to the nearest minute so we never display zero
    const remainingMinutes = scheduledMaintenanceEndTime
      ? Math.ceil((scheduledMaintenanceEndTime - now) / MINUTES)
      : undefined;

    const maintenancePageMessage = !scheduledMaintenanceEndTime
      ? 'App temporary unavailable. Sorry for the inconvenience'
      : remainingMinutes > 0
      ? `The application is currently unavailable due to scheduled maintenance. We expect service to be restored in approximately ${formatDurationDisplay(
          remainingMinutes
        )}. Thank you for your patience.`
      : 'The application is currently unavailable due to scheduled maintenance. We expect service to be restored in the next minutes. Thank you for your patience.';

    return (
      <StyledLayout $isAppVersionDeprecated={isAppVersionDeprecated}>
        <EmptyInvHeader />
        <div className="mt-[30px]">
          <ErrorBar message={maintenancePageMessage} />
        </div>
      </StyledLayout>
    );
  }

  return (
    <StyledLayout $isAppVersionDeprecated={isAppVersionDeprecated}>
      {dynamicParameters.GOOGLE_ANALYTICS_TRACK_ID && (
        <GoogleAnalyticsSnippet appId={dynamicParameters.GOOGLE_ANALYTICS_TRACK_ID} userUiid={user?.uuid} />
      )}
      {dynamicParameters.HOTJAR_APP_ID && !!user && (
        // only render the hotjar snippet if we have hotjar setup AND they're logged in
        <HotjarSnippet appId={dynamicParameters.HOTJAR_APP_ID} sessionUuid={sessionUuid} currentUserUuid={user.uuid} />
      )}
      {dynamicParameters.DRIFT_APP_ID && <DriftSnippet appId={dynamicParameters.DRIFT_APP_ID} />}
      {dynamicParameters.WOOPRA_DOMAIN && !!user && <WoopraSnippet currentUserEmail={user.email} />}
      {showChatWidget() && (
        <>
          <ChatWidgetSnippet src={dynamicParameters.CHAT_WIDGET_URL} />
          <div
            data-public-key={dynamicParameters.CHAT_BOT_API_KEY}
            data-position="bottomright"
            data-user-id={currentUser?.uuid}
            id="chat-widget-container"
          ></div>
        </>
      )}

      {isAppVersionDeprecated && (
        <GlobalBanner>
          <h1>Please refresh this page for the latest version of the application</h1>
        </GlobalBanner>
      )}
      {scheduledMaintenanceStartDate && !isMaintenanceOngoing && (
        <div className="top-0 left-0 right-0 fixed h-[100px] text-center bg-gray-20 text-flint z-[11]">
          <h1>
            {scheduledMaintenanceDurationMinutes
              ? `The application will be unavailable from ${formatDateTimeDisplayWithoutOffset(
                  scheduledMaintenanceStartDate
                )} for approximately ${formatDurationDisplay(
                  scheduledMaintenanceDurationMinutes
                )} due to scheduled maintenance.`
              : `Be aware that the app will be temporary unavailable starting at ${formatDateTimeDisplayWithoutOffset(
                  scheduledMaintenanceStartDate
                )}`}
          </h1>
        </div>
      )}
      {showHeader && showInvHeader && (
        <>
          <InvHeader
            className={classnames({ 'top-[100px]': scheduledMaintenanceStartDate && !isMaintenanceOngoing })}
          />
          {location.pathname !== '/' && <div className="h-[170px]"></div>}
        </>
      )}
      {showHeader && showClassicHeader && (
        <>
          <LayoutHeader currentPath={pathname} $isAppVersionDeprecated={isAppVersionDeprecated} />
          <div className="h-[40px]"></div>
        </>
      )}
      {scheduledMaintenanceStartDate && !isMaintenanceOngoing && <div className="h-[100px]"></div>}
      <LayoutChildren>{children}</LayoutChildren>
      {showFooter && (
        <>
          {location.pathname !== '/' && <div className="h-[40px]"></div>}
          <Footer currentPath={pathname} className={'w-full'} />
        </>
      )}
    </StyledLayout>
  );
};

export default compose(withRouter, withUser)(Layout);
